import * as React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import styled from 'styled-components';

import AppState from '@webapp/store/state';

import { TracInviteTypes } from '@services/invites/types/invite-types';
import { TracInvite } from '@services/invites/types/trac-invite';
import {
  GetInvites,
  CancelInvite,
  ResendInvite,
  AcceptInvite,
  RejectInvite,
  GetRecordLabelInvites,
} from '../../../store/invites/action-creators';
import SimpleUser from '@common/types/user/simple-user';
import { currentInviteList } from '../../../store/invites/selectors';
import LoadingCtrl from '@webapp/controllers/loading';
import { GET_INVITES } from '@webapp/store/invites/actions';
import { AnimatePresence } from 'framer-motion';
import MotionOpacity from '@components/motion/motion-opacity';
import ArtistTeamItem from '@components/artist/artist-team-item';
import Grid from '@components/basics/grid';

interface TeamsListCtrlProps {
  className?: string;
  title?: string;
  type?: TracInviteTypes;
  recordLabelId?: string;
  teamMembers: SimpleUser[];
  confirmRemoveManager?: boolean;
  confirmRemoveMember?: boolean;
  SetConfirmRemoveManager?: React.Dispatch<React.SetStateAction<boolean>>;
  SetConfirmRemoveMember?: React.Dispatch<React.SetStateAction<boolean>>;
  SetSelectedmember: React.Dispatch<
    React.SetStateAction<SimpleUser | undefined>
  >;
}

interface StateProps {
  invites: TracInvite[];
  isLoading: boolean;
  artistId?: string;
  listInvites: (inviterId: string, type: TracInviteTypes) => GetInvites;
  listRecordLabelInvites: (
    type: TracInviteTypes
  ) => GetRecordLabelInvites | undefined;
  cancel: (invite: TracInvite) => CancelInvite;
  resend: (invite: TracInvite) => ResendInvite;
  accept: (invite: TracInvite) => AcceptInvite;
  reject: (invite: TracInvite) => RejectInvite;
}

const mapStateToProps = (
  state: AppState,
  props: TeamsListCtrlProps
): Pick<StateProps, 'invites' | 'isLoading' | 'artistId'> => ({
  invites: props.type
    ? props.recordLabelId
      ? state.invites.recordLabel[props.type].filter(
          (i) => i.status !== 'accepted'
        )
      : currentInviteList(state, props.type).filter(
          (i) => i.status !== 'accepted'
        )
    : state.invites.user,
  isLoading: !!state.ui.loading[GET_INVITES],
  artistId: state.artist.active?.id,
});

const mapDispatchToProps = (dispatch: Dispatch, props: TeamsListCtrlProps) => ({
  listInvites: (inviterId: string, type: TracInviteTypes) =>
    dispatch(new GetInvites(inviterId, type)),
  listRecordLabelInvites: (type: TracInviteTypes) => {
    return props.recordLabelId && props.recordLabelId.length > 0
      ? dispatch(new GetRecordLabelInvites(props.recordLabelId, type))
      : undefined;
  },
  cancel: (invite: TracInvite) => dispatch(new CancelInvite(invite)),
  resend: (invite: TracInvite) => dispatch(new ResendInvite(invite)),
  accept: (invite: TracInvite) => dispatch(new AcceptInvite(invite)),
  reject: (invite: TracInvite) => dispatch(new RejectInvite(invite)),
});

const TeamsListCtrl: React.FC<TeamsListCtrlProps & StateProps> = ({
  recordLabelId,
  type,
  listInvites,
  listRecordLabelInvites,
  artistId,
  ...props
}) => {
  const handleMenu = (action: string, invite: TracInvite) => {
    switch (action) {
      case 'cancel-invite':
        props.cancel(invite);
        return;
      case 'resend-invite':
        props.resend(invite);
        return;
      case 'accept-invite':
        props.accept(invite);
        return;
      case 'reject-invite':
        props.reject(invite);
        return;
      default:
        return;
    }
  };

  return (
    <StyledInvites className={props.className}>
      <AnimatePresence>
        {props.isLoading ? (
          <LoadingCtrl
            className="loading"
            size="80px"
            loading={props.isLoading}
          />
        ) : (
          <MotionOpacity>
            <Grid gap={8}>
              {props.teamMembers.map((teamMember) => (
                <ArtistTeamItem
                  key={teamMember.id}
                  className="teams-item"
                  teamMember={teamMember}
                  label={teamMember.fullName}
                  role={teamMember.role}
                  handleMenu={(action1, invite1) =>
                    handleMenu(action1, invite1)
                  }
                  type={type}
                  confirmRemoveManager={props.confirmRemoveManager}
                  confirmRemoveMember={props.confirmRemoveMember}
                  SetConfirmRemoveManager={props.SetConfirmRemoveManager}
                  SetConfirmRemoveMember={props.SetConfirmRemoveMember}
                  SetSelectedmember={props.SetSelectedmember}
                  teamMemberList={props.teamMembers}
                />
              ))}
            </Grid>
          </MotionOpacity>
        )}
      </AnimatePresence>
    </StyledInvites>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(TeamsListCtrl);

const StyledInvites = styled.div`
  margin-top: 5px;

  .loading > div {
    margin: auto;
  }

  .teams-item {
    margin-bottom: 2px;

    &:last-child {
      margin-bottom: 7px;
    }
  }

  .invites-item {
    margin-bottom: 5px;

    &:last-child {
      margin: 0;
    }
  }
  .item-top-radius {
    border-radius: 4px 4px 0px 0px;
  }
  .item-bottom-radius {
    border-radius: 0px 0px 4px 4px;
  }
  .item-bottom-no-radius {
    border-radius: 0px 0px 0px 0px;
  }
  .item-info {
    background: ${({ theme }) => theme.colors.upload};
    color: ${({ theme }) => theme.colors.white};
    font-size: 12px;
    font-weight: 700;
    word-wrap: break-word;
    display: flex;
    align-items: center;
    border-radius: 0px 0px 4px 4px;
  }
`;
